import { Component, EventEmitter, Output } from "@angular/core";
import { InvitationResource, OrganizationResource, Organization, Account, AccountResource, Invitation } from "@apaq/orgkit-js";
import { PageRequest } from "@apaq/leap-data-core";
import { DialogService } from "../../dialog/dialog.service";
import { LinkService } from "src/app/services/link.service";
import { Roles } from "src/app/common/security/roles";
import { CreateOrganizationDialogComponent } from "../create-organization-dialog/create-organization-dialog.component";
import { ContextService } from "ngx-orgkit";

@Component({
  templateUrl: './organizations-list.component.html',
  styleUrls: ['./organizations-list.component.scss']
})
export class OrganizationsTableComponent {

  @Output() select = new EventEmitter<string>();
  organizations: Organization[] = [];
  invitations: Invitation[] = [];
  _page = 0;
  pageSize = 10;
  totalEntities = 0;
  account?: Account;
  loading = false;
  
  constructor(private linkService: LinkService, private contextService: ContextService,
    private dialogService: DialogService,
    private _organizationsResource: OrganizationResource,
    private _accountResource: AccountResource,
    private invitationResource: InvitationResource) {

      this.contextService.contextChange.subscribe(_ => this.load());
    this.load();
  }

  get totalPages() {
    if(this.totalEntities <= 0) {
      return 1;
    }
    return Math.ceil(this.totalEntities / this.pageSize);
  }

  get page() {
    return this._page;
  }

  set page(value: number) {
    this._page = value;
    this.load();
  }

  async addNew(event: MouseEvent) {
    if(event.altKey) {
      const id = await Stallion.prompt('Enter id', '');
      if(id != null && id.trim() != '') {
        this.contextService.organizationId = id;
      }
      return;
    }

    this.dialogService.open(CreateOrganizationDialogComponent, $localize`Create Organization`)
    .afterClosed().subscribe(async result => {
      if(result === true) {
        await this.contextService.reloadAccount();
        await this.load();
        if(this.organizations.length === 1) {
          this.contextService.organizationId = this.organizations[0].id;
        }
      }
    });
  }

  organizationName(inv: Invitation) {
    return (inv as any).organizationName;
  }

  onClick(organization: Organization) {
    this.contextService.organizationId = organization.id;
    this.linkService.openPlan();
  }

  getRole(organization: Organization): string | undefined {
    const account = this.account!;
    const role = account.roles.find(r => Roles.getOrganizationId(r) === organization.id);
    return role ? Roles.getPermission(role) : undefined;
  }

  getDisplayRole(organization: Organization): string | undefined {
    const account = this.account!;
    const role = account.roles.find(r => Roles.getOrganizationId(r) === organization.id);
    const permission = this.getRole(organization);

    switch(permission) {
      case 'ADMIN':
        return $localize`Administrator`;
      case 'MANAGEMENT':
        return $localize`Manager`;
      case 'USER':
        return $localize`Trusted Employee`;
      case 'EMPLOYEE':
        return $localize`Employee`;
      default:
        return '';
    }
  }

  async leave(organization: Organization) {
    // We need to handle this in a special way.
    // In order to disconnect, we need to delete the current account using the selected orgid.
    // But we do not want to change the context to point towards it as it will cause things to reload.

    if(await Stallion.confirm($localize`Are you sure you want to leave ${organization.name}?`, 'danger', undefined, $localize`Yes, leave ${organization.name}`)) {
      // TODO: Create a new orgkit client using the selected orgid in requestoptions.
      /*const currentOrganizationId = localStorage.getItem(KEY_ORGANIZATION_ID)!;
      localStorage.setItem(KEY_ORGANIZATION_ID, organization.id);
      await this._accountResource?.delete(this.contextService.account);
      localStorage.setItem(KEY_ORGANIZATION_ID, currentOrganizationId);*/
      this.load();
    }
  }

  async delete(organization: Organization) {

    const result = await Stallion.prompt($localize`Are you sure you want to delete ${organization.name} and all the data contained? (This should be a proper delete dialog)`, '', undefined, $localize`Yes, delete ${organization.name}`);
    if(result === organization.name) {
      await this._organizationsResource?.delete(organization);
      this.load();
    }
  }

  async acceptInvitation(inv: Invitation) {
    await this.invitationResource.accept(inv.id);
    this.load();
  }

  private async load() {
    if(this.contextService.account == null) {
      this.organizations = [];
      this.totalEntities = 0;
      return;
    }

    this.loading = true;
    try {
      this.account = await this._accountResource?.findById(this.contextService.account.id);
      let pageRequest: PageRequest= {
        size: this.pageSize,
        page: this.page,
        sort: 'name'
      } as PageRequest

      this.invitations = (await this.invitationResource.findAll({query:{perspective:'Account', accepted: false}} as PageRequest)).content

      const result = await this._organizationsResource!.findAll(pageRequest);
      this.totalEntities = result.totalElements;
      this.organizations = result!.content;
    } finally {
      this.loading = false;
    }

  }
}